<template>
	<v-overlay
		:modelValue="modelValue"
		:persistent="persistent"
		:class="{
			'fullscreen-mode': fullscreen,
			'inset-mode': inset,
			active: modelValue,
		}"
		class="ao-bottom-sheet"
		@update:modelValue="$emit('update:modelValue', $event)"
		@click:outside="handleOverlayClick"
	>
		<template #default>
			<transition
				name="slide-up"
				appear
			>
				<v-sheet
					v-if="modelValue"
					ref="SheetContainerElement"
					class="container"
					elevation="2"
					:style="sheetStyle"
					:class="{ 'persistent-anim': isPersistentAnim }"
					@touchstart="onTouchStart"
					@touchmove="onTouchMove"
					@touchend="onTouchEnd"
				>
					<div
						class="handler"
						:class="{ visible: canSwipeToClose }"
					/>

					<div
						v-if="hasHeader"
						class="header"
					>
						<div class="header__content w-100">
							<slot name="header">
								<span
									v-if="title || $slots['title']"
									class="header__title"
								>
									<slot name="title">
										{{ title }}
									</slot>
								</span>
							</slot>
						</div>
						<AoBtn
							v-if="showCloseButton"
							class="ao-bottom-sheet__close"
							appendIconName="close"
							:iconSize="16"
							@click="close"
						/>
					</div>

					<div
						ref="SheetBodyElement"
						class="body"
						:class="{
							'no-padding': noPadding,
							'd-flex flex-column': bodyFlexLayout,
						}"
					>
						<slot name="default" />
					</div>

					<div
						v-if="hasFooter"
						class="footer"
					>
						<slot name="actions">
							<AoBtn
								v-if="isShowCancelAction"
								class="footer__action"
								:color="applyCancelColor"
								@click="cancel"
							>
								{{ cancelActionText ?? $t('Cancel') }}
							</AoBtn>
							<AoBtn
								v-if="isShowApplyAction"
								class="footer__action"
								:color="applyActionColor"
								@click="$emit('click:apply')"
							>
								{{ applyActionText ?? $t('Apply') }}
							</AoBtn>
						</slot>
					</div>
				</v-sheet>
			</transition>
		</template>
	</v-overlay>
</template>

<script setup>
import { ref, computed, watch, useSlots } from 'vue'

import AoBtn from './AoBtn.vue'

const props = defineProps({
	modelValue: {
		type: Boolean,
	},

	swipeToClose: {
		type: Boolean,
		default: true,
	},

	/* fullscreen имеет приоритет над inset */
	fullscreen: {
		type: Boolean,
		default: false,
	},

	persistent: {
		type: Boolean,
		default: false,
	},

	inset: {
		type: Boolean,
		default: true,
	},

	title: {
		type: String,
		default: null,
	},

	showCloseButton: {
		type: Boolean,
		default: true,
	},

	noPadding: {
		type: Boolean,
		default: false,
	},

	/**
	 * Применяет 'd-flex flex-column' к телу шторки,
	 * позволяя дочерним элементам использовать flex-свойства для растягивания.
	 */
	bodyFlexLayout: {
		type: Boolean,
		default: false,
	},
	isShowCancelAction: {
		type: Boolean,
		default: false,
	},
	cancelActionText: {
		type: String,
	},
	cancelActionColor: {
		type: String,
		// TODO: change to icons-300 color
		default: 'muted',
	},
	isShowApplyAction: {
		type: Boolean,
		default: false,
	},
	applyActionText: {
		type: String,
	},
	applyActionColor: {
		type: String,
		default: 'primary',
	},
})

const emit = defineEmits({
	'update:modelValue': null,
	'click:close': null,
	'click:apply': null,
	'click:cancel': null,
})

const slots = useSlots()

const SheetContainerElement = ref(null)

const SheetBodyElement = ref(null)

const hasHeader = computed(() => {
	return !!(props.title || slots.title || slots.header || props.showCloseButton)
})

const hasFooter = computed(() => {
	return !!slots.actions || props.isShowCancelAction || props.isShowApplyAction
})

const isDragging = ref(false)
const startY = ref(0)
const dragDeltaY = ref(0)
const startTime = ref(0)

const sheetStyle = computed(() => {
	return {
		transform: isDragging.value ? `translateY(${dragDeltaY.value}px)` : '',
		transition: isDragging.value ? 'none' : '',
	}
})

const canSwipeToClose = computed(() => {
	return !props.persistent && props.swipeToClose
})

const close = () => {
	emit('click:close')
	emit('update:modelValue', false)
}

const cancel = () => {
	emit('click:cancel')
	emit('update:modelValue', false)
}

const isPersistentAnim = ref(false)
const ANIM_DURATION = 300

const triggerDenyAnimation = () => {
	if (isPersistentAnim.value) return
	isPersistentAnim.value = true
	setTimeout(() => {
		isPersistentAnim.value = false
	}, ANIM_DURATION)
}

const handleOverlayClick = () => {
	if (props.persistent) {
		triggerDenyAnimation()
	}
}

const onTouchStart = (e) => {
	if (!canSwipeToClose.value) return

	// НЕ устанавливаем isDragging = true здесь.
	// Просто запоминаем начальную позицию.
	// isDragging будет определен в onTouchMove.
	startY.value = e.touches[0].clientY
	startTime.value = performance.now()

	// Сбрасываем deltaY на случай, если предыдущий touchend не сработал
	dragDeltaY.value = 0
}

const onTouchMove = (e) => {
	if (!canSwipeToClose.value) return

	const currentY = e.touches[0].clientY
	const deltaY = currentY - startY.value

	// Если мы уже перетаскиваем шторку, просто продолжаем.
	if (isDragging.value) {
		e.preventDefault()
		// Позволяем тянуть только вниз от начальной точки
		dragDeltaY.value = Math.max(0, deltaY)
		return
	}

	// Это ключевая логика:
	// Мы начинаем перетаскивание шторки ТОЛЬКО ЕСЛИ:
	// 1. Палец движется вниз (deltaY > 0)
	// 2. Контент в теле шторки уже находится в самом верху (scrollTop === 0)
	if (deltaY > 0 && SheetBodyElement.value.scrollTop === 0) {
		// Вот теперь мы захватываем жест!
		isDragging.value = true
		// Предотвращаем стандартное поведение (скролл, pull-to-refresh)
		e.preventDefault()
		dragDeltaY.value = deltaY
	}
	// Если условия не выполнены (например, свайп вверх или скролл вниз по контенту),
	// мы ничего не делаем, и браузер обрабатывает скролл как обычно.
}

const onTouchEnd = () => {
	if (!isDragging.value) return

	const sheetElement = SheetContainerElement.value?.$el
	if (!sheetElement) {
		// Если элемента нет, просто сбрасываем состояние
		isDragging.value = false
		dragDeltaY.value = 0
		return
	}

	const sheetHeight = sheetElement.offsetHeight

	const endTime = performance.now()
	const deltaTime = endTime - startTime.value

	// Рассчитываем скорость в пикселях за миллисекунду
	const velocityY = deltaTime > 0 ? dragDeltaY.value / deltaTime : 0

	// Определяем условия для закрытия
	const distanceThreshold = sheetElement.offsetHeight * 0.4

	// Чем выше шторка, тем выше должен быть порог.
	// Формула подобрана так, чтобы для шторки высотой ~300px порог был ~0.7,
	// а для шторки высотой ~600px - около 1.1.
	// Math.max(0.8, ...) гарантирует, что порог не будет слишком низким.
	const velocityThreshold = Math.max(0.8, Math.min(1.1, 200 / sheetHeight + 0.5))

	// Условие 1: пройдено достаточное расстояние
	const isDistanceSufficient = dragDeltaY.value > distanceThreshold

	// Условие 2: был сделан короткий, но быстрый свайп (flick)
	const isFlickSufficient = velocityY > velocityThreshold

	if (isDistanceSufficient || isFlickSufficient) {
		close()
	}

	// Сбрасываем состояние перетаскивания
	isDragging.value = false
	dragDeltaY.value = 0
}

watch(
	() => props.modelValue,
	(isVisible) => {
		document.body.classList.toggle('body-no-scroll', isVisible)
		document.body.classList.toggle('disable-pull-to-refresh', isVisible)
	},
)
</script>

<style lang="scss">
.ao-bottom-sheet {
	--ao--bottom-sheet-border-radius: 20px;
	--ao--bottom-sheet-padding-side: 12px;
	--ao--bottom-sheet-close-btn-size: 40px;

	& > .v-overlay__content {
		display: flex;
		align-items: flex-end; // Прижимаем контент к низу
		justify-content: center; // Центрируем по горизонтали
		width: 100%;
		height: 100%;
		bottom: 0;
		left: 0;
		transform: none !important;

		// Делаем контейнер "прозрачным" для кликов,
		// чтобы они доходили до фона (.v-overlay__scrim)
		pointer-events: none;
	}

	.v-bottom-sheet__content {
		padding: 0 !important;
		background-color: transparent !important;
		box-shadow: none !important;
	}

	& &__close {
		min-width: var(--ao--bottom-sheet-close-btn-size);
		width: var(--ao--bottom-sheet-close-btn-size);
		height: var(--ao--bottom-sheet-close-btn-size);
		margin-left: auto;
		position: absolute;
		right: 12px;
	}

	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		max-height: calc(var(--ao--visual-viewport-h, 100dvh) - 48px) !important;
		padding-bottom: max(0px, var(--vueuse-safe-area-bottom));
		border-radius: var(--ao--bottom-sheet-border-radius) var(--ao--bottom-sheet-border-radius) 0 0 !important;
		touch-action: pan-y;
		border-radius: 12px 12px 0 0;

		// Возвращаем дочернему элементу (шторке) способность
		// принимать клики и другие события указателя.
		pointer-events: auto;
	}

	&.fullscreen-mode {
		.container {
			max-height: 100vh !important;
			height: 100vh !important;
			height: 100dvh !important;
			border-radius: 0 !important;
		}
	}

	.handler {
		flex-shrink: 0;
		display: flex;
		padding: 8px;
		padding-bottom: 0;
		// cursor: grab;
		opacity: 0;
		transition: opacity 0.3s ease-in-out;

		&.visible {
			opacity: 1;
		}

		&::before {
			content: '';
			display: block;
			width: 36px;
			height: 5px;
			margin: auto;
			border-radius: 4px;
			background-color: #D3D9E0;
		}
	}

	.body {
		overflow: auto;
		min-height: 0;
		flex: 1 1;
		padding: 0 var(--ao--bottom-sheet-padding-side);
		// touch-action: auto;

		&.no-padding {
			padding: 0 !important;
		}
	}

	.header,
	.footer {
		display: flex;
		flex-shrink: 0;
		padding: 8px var(--ao--bottom-sheet-padding-side);
	}

	.header {
		border-bottom: 1px solid #eee;
		align-items: center;
		position: relative;
		padding-right: calc(
			var(--ao--bottom-sheet-close-btn-size) + var(--ao--bottom-sheet-padding-side)
		);

		&__content {
			min-height: 40px;
		}

		&__title {
			font-weight: 600;
			font-size: 16px;
		}
	}

	.footer {
		gap: 12px;
		padding-bottom: 20px;

		&__action {
			flex: 1;
		}
	}

	/* Стили для анимаций Vue */
	.slide-up-enter-active,
	.slide-up-leave-active {
		transition: transform 0.3s ease-in-out;
	}

	.slide-up-enter-from,
	.slide-up-leave-to {
		transform: translateY(100%);
	}

	// Применяем стили 'inset' ТОЛЬКО если НЕТ класса 'fullscreen-mode'.
	&.inset-mode:not(.fullscreen-mode) {
		// И, как и раньше, эти стили работают только на больших экранах.
		@media (min-width: 960px) {
			// Контейнер самой шторки
			.container {
				max-width: 800px;
				margin-bottom: 24px;
				border-radius: var(--ao--bottom-sheet-border-radius) !important; // Закругляем все углы
			}

			// Адаптируем анимацию ухода для inset-режима
			.slide-up-leave-to {
				// Уезжаем вниз с учетом нижнего отступа
				transform: translateY(calc(100% + 24px));
			}
		}
	}

	.persistent-anim {
		animation: persistent-anim 0.3s ease;
	}
}

@keyframes persistent-anim {
	0% {
		transform: translateY(0);
	}
	30% {
		transform: translateY(10px);
	}
	60% {
		transform: translateY(-5px);
	}
	100% {
		transform: translateY(0);
	}
}
</style>
